import React, { useState } from 'react';
import axios from 'axios';
import { API_URL } from '../utils/api';

/**
 * PaymentDetailsModal
 *
 * Where a lien gets paid, and the letter that pays it. Used by both tabs.
 *
 * A health insurer settles by email; a provider agrees a figure on the
 * phone. Neither touches this app, so for both there is no accepted
 * negotiation carrying the agreed amount or the address — the row arrives
 * from Filevine and both facts live in somebody's inbox. Until now there
 * was nowhere to put them, and Ashley opened the payment letter to find out
 * who to pay and where, on every one of these.
 *
 * One component, because the two differ only in where the row came from.
 * Two would be two things to keep in step, and the whole point is that what
 * accounting reads is identical whichever tab the money came off.
 *
 * `row` carries the display name, the agreed figure and what was originally
 * claimed; `save` takes the three values and persists them however that tab
 * does. Everything else is the same.
 *
 * Saving and generating are separate. The details are worth keeping whether
 * or not a letter goes out today, and a letter is worth regenerating from
 * details already checked.
 */
function PaymentDetailsModal({ modal, clientName, close, onSaved }) {
  // `persist`, not `save` — the button handler below is already called
  // save, and the tab supplies how the three values are written.
  const { row, projectId, save: persist, context } = modal;

  const [payee, setPayee] = useState(row.payee || '');
  const [address, setAddress] = useState(row.address || '');
  const [amount, setAmount] = useState(
    row.amount != null && row.amount !== '' ? String(row.amount) : ''
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  const money = (n) =>
    n === null || n === undefined || n === ''
      ? '—'
      : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const num = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
  const ready = payee.trim() && address.trim() && num(amount) > 0;

  const detail = (e) =>
    e.response?.data?.detail || e.message || 'Something went wrong.';

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await persist({
        amount: num(amount),
        payee: payee.trim(),
        address: address.trim(),
      });
      setSaved(true);
      if (onSaved) onSaved();
    } catch (e) {
      setError('Could not save: ' + detail(e));
    } finally {
      setBusy(false);
    }
  };

  const generate = async () => {
    setBusy(true);
    setError(null);
    try {
      // Saved first, always. A letter built from figures that only exist on
      // screen is how a letter and a record come to disagree.
      await persist({
        amount: num(amount),
        payee: payee.trim(),
        address: address.trim(),
      });
      if (onSaved) onSaved();
      const res = await axios.post(
        `${API_URL}/api/projects/${projectId}/letters/lien-payment-manual`,
        {
          payee_name: payee.trim(),
          payee_address_lines: address.split('\n').map((l) => l.trim()).filter(Boolean),
          client_name: clientName,
          amount: num(amount),
          original_lien_amount: row.originally_claimed ?? null,
        },
        { withCredentials: true, responseType: 'blob' }
      );
      const url = URL.createObjectURL(new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Lien Payment Letter_ ${payee.trim()}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setSaved(true);
    } catch (e) {
      let msg = detail(e);
      if (e.response?.data instanceof Blob) {
        try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch (_) { /* keep */ }
      }
      setError('Could not generate the letter: ' + msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    // Rendered inside the case overlay, which closes the case on any click
    // that reaches it. Without this every click in here — a field, a label,
    // anything — bubbled up and threw the preparer back to the case list
    // mid-form.
    <div style={s.backdrop} onClick={(e) => e.stopPropagation()}>
      <div style={s.modal}>
        <div style={s.title}>Payment details</div>
        <div style={s.subtitle}>
          {row.name}
          {row.reference ? ` · ${row.reference}` : ''}
          {row.originally_claimed
            ? ` · originally ${row.claimed_label || 'claimed'} ${money(row.originally_claimed)}`
            : ''}
        </div>

        <div style={s.hint}>
          {context || 'Agreed outside this app, so all three are typed here.'}
          {' '}They stay with the row, and the settlement letter pays whatever
          the final amount says.
        </div>

        <label style={s.label}>Final amount</label>
        <input style={s.input} value={amount} inputMode="decimal"
               onChange={(e) => { setAmount(e.target.value); setSaved(false); }}
               placeholder="What we agreed to pay" />

        <label style={s.label}>Make cheque payable to</label>
        <input style={s.input} value={payee}
               onChange={(e) => { setPayee(e.target.value); setSaved(false); }}
               placeholder={row.name} />

        <label style={s.label}>Mailing address</label>
        <textarea style={s.textarea} rows={3} value={address}
                  onChange={(e) => { setAddress(e.target.value); setSaved(false); }}
                  placeholder={'P.O. Box 5028\nThousand Oaks, CA 91359'} />

        {error && <div style={s.error}>{error}</div>}
        {saved && !error && (
          <div style={s.ok}>
            Saved. Accounting reads these from the case — they do not have to
            open the letter to find them.
          </div>
        )}

        <div style={s.actions}>
          <button style={s.cancel} onClick={close} disabled={busy}>Close</button>
          <button style={s.secondary} onClick={save} disabled={busy || !ready}>
            Save
          </button>
          <button style={s.primary} onClick={generate} disabled={busy || !ready}>
            {busy ? 'Working…' : 'Save & generate letter'}
          </button>
        </div>
      </div>
    </div>
  );
}

const s = {
  backdrop: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.35)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 20,
  },
  modal: {
    background: 'white', borderRadius: 12, padding: '24px 28px',
    width: 'min(520px, 96vw)', maxHeight: '90vh', overflowY: 'auto',
    boxShadow: '0 12px 32px rgba(0,0,0,0.2)',
  },
  title: {
    fontFamily: "'Arial Black', Impact, sans-serif", fontSize: 18,
    color: '#171D29', marginBottom: 4,
  },
  subtitle: { fontSize: 13, color: '#6b7585', lineHeight: 1.5, marginBottom: 10 },
  hint: { fontSize: 11.5, color: '#6b7585', lineHeight: 1.5, marginBottom: 14 },
  label: {
    display: 'block', fontSize: 10, color: '#6b7585', textTransform: 'uppercase',
    letterSpacing: 0.5, fontWeight: 700, marginBottom: 4, marginTop: 12,
  },
  input: {
    width: '100%', boxSizing: 'border-box', padding: '7px 9px',
    border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13,
    color: '#171D29', fontFamily: 'inherit',
  },
  textarea: {
    width: '100%', boxSizing: 'border-box', padding: '8px 10px',
    border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13,
    color: '#171D29', fontFamily: 'inherit', resize: 'vertical',
  },
  error: {
    background: '#fee2e2', color: '#991b1b', padding: '8px 12px',
    borderRadius: 6, marginTop: 12, fontSize: 12, lineHeight: 1.45,
  },
  ok: {
    background: '#ecfdf5', color: '#065f46', padding: '8px 12px',
    borderRadius: 6, marginTop: 12, fontSize: 12, lineHeight: 1.45,
  },
  actions: {
    display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20,
    flexWrap: 'wrap',
  },
  cancel: {
    padding: '8px 14px', border: '1px solid #d1d5db', background: 'white',
    color: '#171D29', borderRadius: 6, fontSize: 13, cursor: 'pointer',
    fontFamily: 'inherit',
  },
  secondary: {
    padding: '8px 14px', border: '1px solid #171D29', background: 'white',
    color: '#171D29', borderRadius: 6, fontSize: 13, cursor: 'pointer',
    fontFamily: 'inherit',
  },
  primary: {
    padding: '8px 16px', border: '1px solid #A1B808', background: '#A1B808',
    color: '#171D29', borderRadius: 6, fontSize: 13, fontWeight: 600,
    cursor: 'pointer', fontFamily: 'inherit',
  },
};

export default PaymentDetailsModal;
